import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Field, inputCls, primaryBtnCls } from "@/components/Kit";
import { Loading } from "@/components/Bits";
import { useAuth } from "@/context/AuthContext";

const EMPTY = { enabled: true, name: "", logo_data: "", address: "", city: "", state: "",
  pincode: "", phone: "", whatsapp: "", email: "", gstin: "", drug_licence: "",
  bill_header: "", bill_footer: "" };

export default function PharmacyIdentity() {
  const { can } = useAuth();
  const canManage = can("settings.manage");
  const [form, setForm] = useState(null);
  const [base, setBase] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/settings/module-profile/pharmacy")
      .then(({ data }) => { setForm({ ...EMPTY, ...(data.profile || {}) }); setBase(data.base || {}); })
      .catch((e) => toast.error(apiError(e)));
  }, []);

  if (!form) return <Loading />;
  const set = (k, v) => setForm({ ...form, [k]: v });

  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.put("/settings/module-profile/pharmacy", form);
      toast.success("Pharmacy identity saved. It now appears on pharmacy bills, PDFs and WhatsApp.");
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const uploadLogo = (file) => {
    if (!file) return;
    if (file.size > 1.5 * 1024 * 1024) { toast.error("Logo must be under 1.5 MB."); return; }
    const r = new FileReader();
    r.onload = () => set("logo_data", r.result);
    r.readAsDataURL(file);
  };

  return (
    <form onSubmit={save} className="space-y-6" data-testid="pharmacy-identity-form">
      <div className="rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-800">
        This is the Pharmacy module's own billing identity, stored under this business. It overrides the
        parent business identity on pharmacy POS bills, PDFs/prints and pharmacy WhatsApp invoices only —
        Hospital and Lab identity are not affected. Empty fields fall back to the parent business.
      </div>

      <label className="flex items-center justify-between gap-3 rounded-md border border-slate-200 px-4 py-3 text-sm">
        <span className="font-semibold text-slate-800">Use a pharmacy-specific identity on pharmacy bills</span>
        <input type="checkbox" checked={form.enabled} data-testid="pharmacy-identity-enabled"
          onChange={(e) => set("enabled", e.target.checked)} className="h-4 w-4 accent-[#0F172A]" />
      </label>

      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Pharmacy / business name">
          <input className={inputCls} value={form.name} data-testid="pharmacy-identity-name"
            placeholder={base.name || "e.g. AJ Classic Pharmacy"} onChange={(e) => set("name", e.target.value)} />
        </Field>
        <Field label="Contact number">
          <input className={inputCls} value={form.phone} data-testid="pharmacy-identity-phone"
            placeholder={base.phone || "+91 …"} onChange={(e) => set("phone", e.target.value)} />
        </Field>
        <Field label="WhatsApp number">
          <input className={inputCls} value={form.whatsapp} data-testid="pharmacy-identity-whatsapp"
            placeholder={base.whatsapp || "+91 …"} onChange={(e) => set("whatsapp", e.target.value)} />
        </Field>
        <Field label="Email">
          <input className={inputCls} value={form.email} data-testid="pharmacy-identity-email"
            placeholder={base.email || "pharmacy@…"} onChange={(e) => set("email", e.target.value)} />
        </Field>
        <Field label="Address" className="sm:col-span-2">
          <input className={inputCls} value={form.address} data-testid="pharmacy-identity-address"
            placeholder={base.address || "Shop / building, street"} onChange={(e) => set("address", e.target.value)} />
        </Field>
        <Field label="City">
          <input className={inputCls} value={form.city} placeholder={base.city}
            onChange={(e) => set("city", e.target.value)} />
        </Field>
        <Field label="State">
          <input className={inputCls} value={form.state} placeholder={base.state}
            onChange={(e) => set("state", e.target.value)} />
        </Field>
        <Field label="PIN code">
          <input className={inputCls} value={form.pincode} placeholder={base.pincode}
            onChange={(e) => set("pincode", e.target.value)} />
        </Field>
        <Field label="Drug Licence number (optional)">
          <input className={inputCls} value={form.drug_licence} data-testid="pharmacy-identity-dl"
            placeholder={base.drug_licence || "MH-PUN-20B-…"} onChange={(e) => set("drug_licence", e.target.value)} />
        </Field>
        <Field label="GST number (optional)">
          <input className={inputCls} value={form.gstin} data-testid="pharmacy-identity-gstin"
            placeholder={base.gstin || "27ABCDE1234F1Z5"} onChange={(e) => set("gstin", e.target.value)} />
        </Field>
        <Field label="Bill header (optional)">
          <input className={inputCls} value={form.bill_header} data-testid="pharmacy-identity-header"
            placeholder="e.g. Retail & Wholesale Chemist" onChange={(e) => set("bill_header", e.target.value)} />
        </Field>
        <Field label="Bill footer / terms (optional)" className="sm:col-span-2">
          <textarea rows={2} className={inputCls} value={form.bill_footer} data-testid="pharmacy-identity-footer"
            placeholder="Printed at the bottom of every pharmacy bill." onChange={(e) => set("bill_footer", e.target.value)} />
        </Field>
        <Field label="Logo" className="sm:col-span-2">
          <div className="flex items-center gap-4">
            {form.logo_data && <img src={form.logo_data} alt="logo" className="h-14 w-14 rounded object-contain border border-slate-200" />}
            <input type="file" accept="image/*" data-testid="pharmacy-identity-logo"
              className="text-sm text-slate-700" onChange={(e) => uploadLogo(e.target.files?.[0])} />
            {form.logo_data && <button type="button" className="text-xs text-red-600" onClick={() => set("logo_data", "")}>Remove</button>}
          </div>
        </Field>
      </div>

      {canManage && (
        <button className={primaryBtnCls} disabled={busy} data-testid="pharmacy-identity-save-btn">
          {busy ? "Saving…" : "Save pharmacy identity"}
        </button>
      )}
    </form>
  );
}
